<template>
  <div>
    <div class="chat-toggle" @click="toggleChat" v-if="!getIsMobile">
      Chat <span class="arrow">{{ isChatOpen ? "▼" : "▲" }}</span>
      <span class="online-indicator">● Online</span>
    </div>

    <div class="chat-icon" @click="toggleChat" v-else>
      <div class="icon-wrapper">
        <div class="icon-block">
          <i class="fa fa-comments icon"></i>
        </div>
        <span class="icon-label">Chat</span>
      </div>
    </div>
    <div class="chat-container" :class="{ open: isChatOpen }">
      <div class="chat-header">
        <span>Chat <span class="online-indicator">● Online</span></span>
        <div class="button-group">
          <button @click="goBackToChat" title="Back to Chat">⬅️</button>
          <button @click="toggleChat" title="Minimize Chat">➖</button>
          <button @click="closeChat" title="Close Chat">✖</button>
        </div>
      </div>
      <div class="chat-header-bar-container" v-if="!chatUrl">
        <div class="chat-header-bar">
          <div class="chat-title-group">
            <span class="chat-label">Chat</span>
            <i
              class="el-icon-plus chat-add-icon"
              @click="toggleAccordion"
              :class="{ rotated: accordionOpen }"
            />
          </div>
          <div
            class="spectacledcoder-search-bar"
            :class="{ expanded: searchFocused }"
            v-show="!accordionOpen"
          >
            <img
              class="search-icon"
              width="30"
              height="30"
              src="https://img.icons8.com/sf-black/500/000000/search.png"
              alt="search"
            />
            <input
              type="text"
              name="search"
              placeholder="Search..."
              class="spectacledcoder-search-input"
              autocomplete="on"
              v-model="searchQuery"
              @input="filterChatHistory"
            />
          </div>
        </div>

        <transition name="accordion">
          <div class="msg-board" v-if="!accordionOpen">
            <div
              class="chat-panel chat-user-list-panel"
              @scroll="handleScroll"
              style="overflow-y: scroll; max-height: 70vh"
            >
              <ul
                class="chat-list"
                v-if="filteredRooms.length"
                @scroll="handleScroll"
                style="
                  overflow-y: scroll;
                  max-height: 70vh;
                  padding-right: 8px;
                  box-shadow: inset 0 -8px 8px -8px rgba(0, 0, 0, 0.08);
                "
              >
                <li
                  v-for="room in filteredRooms"
                  :key="room._id || room.roomID"
                  class="chat-item-card"
                  @click="openChat(room.roomID)"
                  :title="room.users.join(', ')"
                >
                  <a href="#">
                    <ul class="chat-detail">
                      <li>
                        <div
                          class="chat-profile-user"
                          :style="`background: url(${
                            room.users.length > 2
                              ? defaultGroupImage
                              : room.users.length === 2
                              ? room.users[0]?.profileImage || defaultAvatar
                              : defaultAvatar
                          }) no-repeat center center / cover;`"
                        ></div>
                      </li>
                      <li>
                        <div class="chat-user-nm">
                          {{ room.roomName || "Group Chat" }}
                        </div>
                        <div class="chat-desc">
                          {{ getSubScription(room) }}
                        </div>
                      </li>
                      <li>
                        <div class="chat-seen">
                          {{ formatTimestamp(room.lastMessageTimestamp) }}
                        </div>
                        <div class="not-read-label" v-if="room.unreadCount > 0">
                          {{ room.unreadCount }}
                        </div>
                      </li>
                    </ul>
                  </a>
                </li>
              </ul>
              <div
                v-else
                class="no-chat-message"
                style="text-align: center; padding: 20px; color: #888"
              >
                <p>No conversations yet.</p>
                <p>Click <strong>+</strong> above to start a new chat!</p>
              </div>

              <div v-if="loading" class="chat-loader">
                <div class="loader"></div>
                <p>Loading more chats...</p>
              </div>
              <div
                v-else-if="allLoaded"
                class="chat-end-message"
                style="text-align: center; padding: 10px; color: #999"
              >
                <p>📦 You've reached the end of chat history</p>
              </div>
            </div>
          </div>
        </transition>

        <transition name="accordion">
          <div v-show="accordionOpen" class="chat-form-accordion">
            <el-form
              :model="form"
              :rules="rules"
              ref="formRef"
              label-position="top"
            >
              <el-form-item label="Conversation Name" prop="roomName">
                <el-input v-model="form.roomName" />
              </el-form-item>
              <el-form-item label="Participants" prop="users">
                <el-select
                  v-model="form.users"
                  multiple
                  filterable
                  class="chat-user-select"
                  placeholder="Type to search users"
                  collapse-tags
                >
                  <el-option
                    v-for="user in filteredUsers"
                    :key="user._id"
                    :label="formatUserLabel(user)"
                    :value="user._id"
                  >
                    <div class="chat-option-item">
                      <img
                        :src="user.profileImage || defaultAvatar"
                        class="chat-user-avatar"
                      />
                      <span class="chat-user-name">
                        {{ formatUserLabel(user) }}
                      </span>
                    </div>
                  </el-option>
                </el-select>
              </el-form-item>

              <el-button
                type="primary"
                @click="submitForm('formRef')"
                :loading="submitting"
                size="mini"
                icon="el-icon-chat-line-round"
                :disabled="form.users.length === 0 || creatingRoom"
                class="chat-start-button"
              >
                Start Conversation
              </el-button>
            </el-form>
          </div>
        </transition>
      </div>

      <div v-else class="chat-frame-container">
        <iframe
          :src="chatUrl"
          class="chat-frame"
          @load="onIframeLoaded"
          @error="onIframeError"
        ></iframe>
        <div v-if="iframeLoading" class="iframe-loader">
          <div class="loader"></div>
          <p>Loading chat room...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Config from "@/config/app";
import {
  initSocket,
  onSocketEvent,
  offSocketEvent,
} from "@/utils/socketManager";
import { postAPICall } from "../helpers/httpHelper";

export default {
  data() {
    return {
      allLoaded: false,
      allDataLoaded: false,
      searchQuery: "",
      filteredRooms: [],
      getCompanyUsersForInternalchat: [],
      allRooms: [],
      displayRooms: [],
      currentPage: 1,
      itemsPerPage: 10,
      isChatOpen: false,
      users: [],
      filteredUsers: [],
      loading: false,
      selectLoading: false,
      submitting: false,
      creatingRoom: false,
      chatRoomId: null,
      chatUrl: "",
      iframeLoading: false,
      historyMessages: [
        "Support - Last chat: 2h ago",
        "Tech Support - 3 days ago",
      ],
      // defaultGroupImage: 'https://cdn-icons-png.flaticon.com/512/745/745256.png',
      defaultGroupImage:
        "data:image/png;base64,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",
      defaultAvatar: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
      accordionOpen: false,
      searchFocused: false,
      form: {
        roomName: "",
        users: [],
      },
      rules: {
        roomName: [
          {
            required: true,
            message: "Please provide a conversation name",
            trigger: "blur",
          },
          {
            min: 3,
            max: 50,
            message: "Name length should be between 3 and 50 characters",
            trigger: "blur",
          },
        ],
        users: [
          {
            type: "array",
            required: true,
            message: "Please select at least one participant",
            trigger: "change",
          },
          {
            type: "array",
            required: true,
            message: "Please select at least one participant",
            trigger: "blur",
          },
        ],
      },
    };
  },

  computed: {
    ...mapGetters("auth", [
      "getActiveWorkspace",
      "getAuthenticatedUser",
      "getCompanyUsers",

      "getActiveContactType",
    ]),
    ...mapGetters("navigationOpen", ["getIsMobile", "getIsRSOpen"]),
    ...mapGetters("company", ["getContactLoginSlug"]),
    totalPages() {
      return Math.ceil(this.allRooms.length / this.itemsPerPage);
    },
  },

  created() {
    this.userInfo = this.getAuthenticatedUser;
  },

  async mounted() {
    console.log("contactTypeId", this.getAuthenticatedUser);
    console.log("getActiveContactType", this.getActiveContactType);
    this.filteredRooms = [...this.allRooms];
    this.displayRooms = [...this.allRooms];
    await this.initSocketForUser();
  },

  methods: {
    handleScroll(event) {
      const target = event.target;
      const scrollThreshold = 50;

      if (
        target.scrollTop + target.clientHeight >=
        target.scrollHeight - scrollThreshold
      ) {
        if (!this.loading && !this.allLoaded) {
          this.getChatHistory();
        }
      }
    },

    getSafeCompanyId() {
      return (
        this.getActiveWorkspace?.company_id ||
        this.getAuthenticatedUser?.company_id ||
        "defaultCompanyId"
      );
    },

    async submitForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (!valid) return false;
        this.submitting = true;

        if (this.form.users.length === 0) {
          this.$message.error("Select at least one user");
          this.submitting = false;
          return;
        }
        this.creatingRoom = true;
        this.loading = true;
        this.iframeLoading = false;
        const safeCompanyId = this.getSafeCompanyId();

        const participants = [
          {
            username: this.getAuthenticatedUser.first_name.toLowerCase(),
            userID: this.getAuthenticatedUser._id + safeCompanyId,

            displayName: `${this.getAuthenticatedUser.first_name} ${this.getAuthenticatedUser.last_name}`,
            gender: this.getAuthenticatedUser.gender || "male",
          },
          ...this.form.users.map((userId) => {
            const user = this.users.find((u) => u._id === userId);
            return {
              username: user.first_name.toLowerCase(),
              userID: user._id + safeCompanyId,

              displayName: `${user.first_name} ${user.last_name}`,
              gender: user.gender || "male",
            };
          }),
        ];
        try {
          const response = await axios.post(
            `${Config.CHAT_API_SOCKET_URL}/api/rooms`,
            {
              roomName: this.form.roomName || "Group Chat",
              participates: participants.map((user) => ({
                userID: user.id || user.userID || "string",
                displayName: user.name || user.displayName || "string",
                profile: user.profile || "",
              })),
              metaData: {},
            },
            {
              headers: {
                "Content-Type": "application/json",
                accept: "*/*",
              },
            }
          );
          if (response.data) {
            this.chatRoomId = response.data.data._id;
            const primaryColor = getComputedStyle(document.documentElement)
              .getPropertyValue("--primary-color")
              .trim();

            const companyId = this.getActiveWorkspace?.company_id;
            const contactTypeId = this.getAuthenticatedUser?.company_id;
            const userID =
              this.getAuthenticatedUser._id + (companyId || contactTypeId);

            this.chatUrl =
              `${Config.CHAT_SOCKET_URL}/chat` +
              `?authID=${
                this.authToken ||
                "21cc5e96f2736c81673fb94587c90b87c90b7f5dbd03780039349dfeb1e3457d"
              }` +
              `&roomID=${this.chatRoomId}` +
              `&userID=${userID}` +
              `&barColor=${this.barColor || primaryColor || "#1B487E"}` +
              `&senderBg=${this.senderBg || primaryColor || "#1B487E"}` +
              `&receiverBg=${this.receiverBg || "#FFFFFF"}` +
              `&textColor=${this.textColor || "#000000"}` +
              `&fontFamily=${encodeURIComponent(
                this.fontFamily || "Lucida Console"
              )}`;

            this.loading = false;
            this.creatingRoom = false;
            this.iframeLoading = false;
            this.form.roomName = "";
            this.form.users = [];
            this.accordionOpen = false;
            this.submitting = false;
            this.$message.success(
              "Chat Room Created! Loading chat interface..."
            );
          }
        } catch (error) {
          this.$message.success("Failed to create chat room");
          this.loading = false;
          this.creatingRoom = false;
          this.iframeLoading = false;
          this.form.roomName = "";
          this.form.users = [];
          this.accordionOpen = false;
          this.submitting = false;
        }
      });
    },

    resetForm() {
      this.form = {
        roomName: "",
        users: [],
      };
    },
    toggleAccordion() {
      this.accordionOpen = !this.accordionOpen;
    },

    formatUserLabel(user) {
      const fullName = `${user.first_name || ""} ${
        user.last_name || ""
      }`.trim();

      let contactTypeLabel = "";
      if (typeof user.contact_type === "string") {
        contactTypeLabel = user.contact_type;
      } else if (
        typeof user.contact_type === "object" &&
        user.contact_type !== null
      ) {
        contactTypeLabel = user.contact_type.name || "";
      }

      let roleTitle = "";
      let userTypeName = "";

      if (Array.isArray(user.rolesList) && user.rolesList.length > 0) {
        const firstRole = user.rolesList[0];
        roleTitle = firstRole?.role_id?.title || "";
        userTypeName = firstRole?.userType?.name || "";
      }

      const labelParts = [];
      if (contactTypeLabel) labelParts.push(contactTypeLabel);
      if (roleTitle) labelParts.push(roleTitle);
      if (userTypeName) labelParts.push(userTypeName);

      const entity = labelParts.length ? ` (${labelParts.join(" - ")})` : "";

      let truncatedName = fullName;
      if (fullName.length > 50) {
        truncatedName = fullName.substring(0, 50) + "...";
      }

      return `${truncatedName}${entity}`;
    },
    getSubScription(room) {
      let usersList = room.users.join(", ");
      const count = this.getIsMobile ? 25 : 45;
      if (usersList.length > count) {
        return usersList.substring(0, count) + "...";
      }
      return usersList;
    },
    filterChatHistory() {
      const query = this.searchQuery.trim().toLowerCase();
      if (query === "") {
        this.filteredRooms = [...this.allRooms];
      } else {
        this.filteredRooms = this.allRooms.filter(
          (room) =>
            room.roomName.toLowerCase().includes(query) ||
            room.users.some((user) => user.toLowerCase().includes(query))
        );
      }
    },
    closeChat() {
      this.isChatOpen = false;
      this.chatUrl = "";
      this.resetForm();
    },

    goBackToChat() {
      this.chatUrl = "";
      this.allRooms = [];
      this.filteredRooms = [];
      this.displayRooms = [];
      this.currentPage = 1;
      this.allLoaded = false;
      this.allDataLoaded = false;
      this.getChatHistory();
      this.resetForm();
    },

    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const now = new Date();

      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();

      if (isToday) {
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      } else if (isYesterday) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    },
    getInitials(users) {
      if (users.length == 1) {
        return users
          .map((user) =>
            user
              .split(" ")
              .filter(Boolean)
              .map((word) => word[0])
              .join("")
              .toUpperCase()
          )
          .join("");
      } else {
        return users.map((user) => user.charAt(0).toUpperCase()).join("");
      }
    },
    openChat(roomID) {
      this.iframeLoading = true;
      this.chatRoomId = roomID;

      const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--primary-color")
        .trim();
      // const hexColor = primaryColor.startsWith("#") ?
      //   primaryColor.slice(1) // Removes #
      //   :
      //   primaryColor
      //     .match(/\d+/g)
      //     .map((x) => (+x).toString(16).padStart(2, "0"))
      //     .join("")
      //     .toUpperCase();

      const companyId = this.getActiveWorkspace?.company_id;
      const contactTypeId = this.getAuthenticatedUser?.company_id;
      const userID =
        this.getAuthenticatedUser._id + (companyId || contactTypeId);

      // this.chatUrl = `${Config.CHAT_SOCKET_URL}/chat?authID=21cc5e96f2736c81673fb94587c90b87c90b7f5dbd03780039349dfeb1e3457d&roomID=${this.chatRoomId}&userID=${userID}&primary=${hexColor}`;

      this.chatUrl =
        `${Config.CHAT_SOCKET_URL}/chat` +
        `?authID=${
          this.authToken ||
          "21cc5e96f2736c81673fb94587c90b87c90b7f5dbd03780039349dfeb1e3457d"
        }` +
        `&roomID=${this.chatRoomId}` +
        `&userID=${userID}` +
        `&barColor=${this.barColor || primaryColor || "#1B487E"}` +
        `&senderBg=${this.senderBg || primaryColor || "#1B487E"}` +
        `&receiverBg=${this.receiverBg || "#FFFFFF"}` +
        `&textColor=${this.textColor || "#000000"}` +
        `&fontFamily=${encodeURIComponent(
          this.fontFamily || "Lucida Console"
        )}`;
    },
    async getChatHistory() {
      try {
        this.loading = true;

        const userID = this.getAuthenticatedUser._id;
        const companyID =
          this.getActiveWorkspace?.company_id ||
          this.getAuthenticatedUser?.company_id;

        const url = `${Config.CHAT_API_SOCKET_URL}/api/rooms/user_by_page/${userID}${companyID}?page=${this.currentPage}&limit=${this.itemsPerPage}`;

        const response = await axios.get(url, {
          headers: {
            accept: "application/json",
            Authorization: `Bearer `, // Optional if secured
          },
        });

        const rooms = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        const totalPages = response.data.totalPages || 1;

        const normalizedRooms = rooms.map((room) => ({
          ...room,
          lastMessage: room.lastMessage?.[0] || null,
          lastMessageTimestamp: room.lastMessageTimestamp?.[0] || null,
          unreadCount: Array.isArray(room.unreadCount)
            ? room.unreadCount.length
            : 0,
        }));

        const mergedRooms = [...this.allRooms, ...normalizedRooms];

        const uniqueRooms = mergedRooms.filter(
          (room, index, self) =>
            index === self.findIndex((r) => r.roomID === room.roomID)
        );

        this.allRooms = uniqueRooms;
        this.filteredRooms = [...uniqueRooms];

        this.displayRooms = [...this.filteredRooms];

        if (this.currentPage >= totalPages) {
          this.allDataLoaded = true;
        }

        this.currentPage++;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error("Failed to fetch paginated chat history:", error);
      }
    },

    paginateRooms() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.displayRooms = this.allRooms.slice(start, end);
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.paginateRooms();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.paginateRooms();
      }
    },

    goToPage(page) {
      this.currentPage = page;
      this.paginateRooms();
    },

    async getChatInfo() {
      await Promise.all([this.getCompanyUsersList(), this.getChatHistory()]);
    },

    toggleChat() {
      if (!this.isChatOpen) {
        this.getChatInfo();
      }
      this.isChatOpen = !this.isChatOpen;
    },
    onIframeLoaded() {
      this.iframeLoading = false;
    },
    onIframeError() {
      this.$notify.error({
        title: "Error",
        message: "Failed to load chat interface",
      });
      this.iframeLoading = false;
      this.chatUrl = "";
    },
    async getCompanyUsersList() {
      this.loading = true;
      try {
        this.getCompanyUsersForInternalchat = await postAPICall(
          "GET",
          "/user/companyUsersForInternalchat"
        );
        if (
          this.getCompanyUsersForInternalchat &&
          this.getCompanyUsersForInternalchat.data
        ) {
          this.users = this.getCompanyUsersForInternalchat.data;

          if (this.getAuthenticatedUser && this.getAuthenticatedUser._id) {
            this.filteredUsers = this.users.filter(
              (user) => user._id !== this.getAuthenticatedUser._id
            );
          } else {
            this.filteredUsers = this.users;
          }
          this.loading = false;
        }
      } catch (err) {
        this.loading = false;
        console.error("Failed to load company users:", err);
      }
    },
    filterUsers(query) {
      if (query) {
        this.filteredUsers = this.users.filter((user) =>
          `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(query.toLowerCase())
        );
      } else {
        this.filteredUsers = [...this.users];
      }
    },
    async initSocketForUser() {
      const userID =
        this.getAuthenticatedUser._id +
        (this.getActiveWorkspace?.company_id ||
          this.getAuthenticatedUser?.company_id);

      initSocket("chat", Config.CHAT_API_SOCKET_URL, {
        query: { userID },
        path: "/socket.io",
      });
      onSocketEvent(
        "chat",
        `UserNotification_${userID}`,
        this.handleNotification
      );
    },
    handleNotification(payload) {
      // console.log("🔔 New message received:", payload);
      if (!this.isChatOpen) {
        this.isChatOpen = true;
        this.openChat(payload.roomID);
      } else {
        this.$notify({
          title: "New Message",
          message: payload.content,
          type: "info",
        });
      }
    },
  },
  beforeDestroy() {
    offSocketEvent("chat", "UserMessageNotification");
  },
};
</script>

<style scoped>
.el-select {
  width: 80%;
}

.start-conversation {
  width: 15%;
}

.chat-icon i {
  font-size: 28px;
}

@media screen and (max-width: 375px) {
  .chat-icon {
    right: 12px;
    width: 44px;
    height: 44px;
  }

  .chat-icon i {
    font-size: 26px;
  }
}

.chat-history {
  background: #f5f5f5;
  padding: 12px 15px;
  margin-top: 15px;
  border-radius: 8px;
  font-size: 14px;
  color: #555;
}

.chat-history h3,
.chat-history h4 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.chat-history ul {
  list-style: none;
  padding: 0;
}

.chat-message {
  background: #e1eaf6;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 5px;
}

.search-container {
  position: relative;
  width: 100%;
}

.chat-search {
  width: 100%;
  padding: 5px 5px 5px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  transition: 0.3s ease-in-out;
}

.chat-search:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0px 0px 5px rgba(0, 123, 255, 0.5);
}

.chat-list {
  max-height: 50vh;
  overflow-y: scroll;
  padding-right: 5px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-list::-webkit-scrollbar {
  width: 6px;
}

.chat-list::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 5px;
}

.chat-card {
  padding: 12px 15px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chat-card:hover {
  background: #f1f1f1;
}

.chat-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.user-avatar {
  width: 50px;
  height: 50px;
  background-color: #007bff;
  color: white;
  font-size: 18px;
  font-weight: bold;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  margin-right: 10px;
}

.chat-details {
  flex-grow: 1;
  padding: 0 10px;
  display: flex;
  flex-direction: column;
}

.chat-name {
  font-weight: bold;
  font-size: 16px;
  color: #0078d7;
}

.chat-id {
  font-size: 12px;
  color: #666;
}

.user-count {
  font-size: 13px;
  color: gray;
}

.chat-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  font-size: 12px;
  color: #777;
}

.timestamp {
  font-size: 12px;
  color: gray;
}

.no-chats {
  color: #888;
  text-align: center;
  margin-top: 10px;
  padding: 15px 0;
}

.chat-container {
  position: fixed;
  bottom: 0;
  right: 20px;
  width: 0;
  height: 90vh;
  background: #ffffff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  transition: width 0.3s ease-in-out;
  display: flex;
  flex-direction: column;

  border-radius: 1em 1em 0px 1em;
  z-index: 999;
}

.chat-container.open {
  padding: 2px;
  width: 35%;
}

.chat-header {
  background: #f4f4f4;
  color: #ffa500;
  padding: 1%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px 12px 0 0;
}

.chat-header button {
  background: none;
  border: none;
  font-size: 16px;
  color: #ff4d4f;
  cursor: pointer;
  padding: 5px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease-in-out;
  margin-left: 5px;
}

.chat-header button:hover {
  background: rgba(255, 77, 79, 0.2);
}

.button-group {
  display: flex;
  gap: 5px;
}

.chat-frame-container {
  position: relative;
  width: 100%;
  height: 100vh;

  overflow: hidden;
}

.chat-frame {
  width: 100%;
  height: 100%;
  border: none;
}

.online-indicator {
  font-size: 14px;
  margin-left: 8px;
  color: #90ee90;
}

.chat-btn {
  margin-top: 2%;
  width: 100%;
  background: #0078d7;
  color: white;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  border-radius: 8px;
  font-weight: bold;
}

.chat-btn:disabled {
  background: #bbb;
  cursor: not-allowed;
}

.iframe-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.iframe-loader .loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0078d7;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.iframe-loader p {
  color: #0078d7;
  font-weight: bold;
}

.chat-toggle {
  position: fixed;
  bottom: 0;
  right: 20px;
  width: 220px;
  height: 45px;
  background: var(--primary-color) !important;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease-in-out;
}

.arrow {
  margin-left: 8px;
}

.chat-form {
  padding: 1%;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media screen and (max-width: 768px) {
  .chat-container {
    display: none;
    width: 100%;
    height: 90vh !important;
    border-radius: 0;
    bottom: 0;
    right: 0;
    overflow-y: auto;
  }

  .chat-container.open {
    display: block;
    width: 100%;
    height: 80vh;
    padding: 0;
  }

  .chat-list {
    max-height: 50vh;
    overflow-y: scroll;
    padding-right: 5px;
  }

  .chat-frame-container {
    height: calc(100vh - 50px);
  }

  .chat-frame {
    height: 100%;
  }

  .chat-toggle {
    width: 100%;
    right: 0;
    bottom: 0;
    border-radius: 0;
  }

  .chat-toggle:focus {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.5);
  }

  .chat-header {
    font-size: 16px;
    padding: 10px;
  }

  .iframe-loader {
    height: 80vh;
  }

  .chat-box {
    display: none;
    position: fixed;
    bottom: 80px;
    right: 10px;
    width: 90%;
    max-height: 80%;
  }
}

@media screen and (max-width: 375px) {
  .chat-container {
    display: none;
  }
}

.chat-initiation-panel {
  padding: 20px;
  background: #fdfdfd;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chat-label {
  font-weight: 600;
  font-size: 15px;
  color: #333;
}

.chat-user-select {
  width: 100%;
}

.chat-option-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: #ddd;
}

.chat-user-name {
  font-weight: 500;
  color: #333;
}

.chat-start-button {
  width: 100%;
  font-weight: bold;
  height: 40px;
}

.chat-history {
  background: #ffffff;
  padding: 15px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #333;
  }

  .chat-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 50vh;
    overflow-y: scroll;
  }

  .chat-item-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.05em 0.75em;
    background-color: #f9f9f9;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #eef6ff;
      border-color: #cce4ff;
    }
  }

  .chat-avatar {
    background-color: #0078d7;
    color: #fff;
    font-size: 16px;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .chat-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .chat-title {
    font-size: 14px;
    font-weight: 600;
    color: #222;
  }

  .chat-subtitle {
    font-size: 12px;
    color: #888;
  }

  .chat-timestamp {
    font-size: 11px;
    color: #999;
    white-space: nowrap;
    margin-left: 12px;
    flex-shrink: 0;
  }

  .no-chats {
    text-align: center;
    color: #aaa;
    padding: 15px;
    font-size: 14px;
  }
}

.mb-1 {
  margin-bottom: 1em;
}

.msg-board.show-grp .create-group-panel,
.msg-board.show-grp-2 .create-group-panel {
  z-index: 1;
  background: #fff;
  box-shadow: 0px 0px 8px #ddd;
}

.create-grp-scr-2,
.create-grp-scr-3 {
  display: none;
}

.msg-board.show-grp .create-grp-scr-2,
.msg-board.show-grp-2 .create-grp-scr-3 {
  z-index: 1;
  display: block;
}

.chat-user-list-panel {
  width: 100%;
  background: #f6f6f6;
  border-radius: 5px;
  display: inline-block;
}

.add-group-btn {
  padding: 5px 5px;
  text-align: center;
  float: right;
}

.add-group-btn a {
  color: #2a2a2a;
  font-size: 20px;
}

.chat-detail li {
  display: inline-block;
  vertical-align: middle;
}

.chat-list .chat-detail li:last-child {
  text-align: right;
  float: right;
}

.chat-user-nm {
  color: #2a2a2a;
}

.chat-desc,
.chat-seen {
  color: #808080;
}

.chat-profile-user {
  margin-right: 10px;
  height: 50px;
  width: 50px;
  border: 2px solid #b0b0b0;
  border-radius: 50px;
}

.chat-user-nm {
  font-weight: 500;
  font-size: 16px;
  line-height: 25px;
}

.chat-list {
  max-height: 80vh;
  overflow-y: scroll;
  padding-bottom: 2em;
}

.chat-list::-webkit-scrollbar-thumb {
  background: #1d437e;
  border-radius: 10px;
  -webkit-border-radius: 10px;
}

.chat-list::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.chat-list::-moz-scrollbar-thumb {
  background: #1d437e;
  border-radius: 10px;
  -webkit-border-radius: 10px;
}

.chat-list::-moz-scrollbar {
  height: 6px;
  width: 6px;
}

.chat-list li a {
  display: block;
  padding: 0.25em 0.35em;
  display: block;
  border-bottom: 1px solid #d9d7d766;
}

.chat-list li a:hover {
  background: #ffffff;
}

.not-read-lable {
  background: #e12026;
  font-size: 12px;
  color: #ffffff;
  height: 20px;
  width: 20px;
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spectacledcoder-search-bar {
  width: 40px;
  height: 40px;
  background-color: white;
  display: flex;
  border-radius: 0% 30px 30px 30px;
  transition: width 1s;
  box-shadow: 5px 5px 20px #d8e6fd, -5px -5px 30px white;
  float: inline-end;
}

.search-icon {
  padding: 5px;
  margin-bottom: auto;
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  border-radius: 50%;
  background-color: #e9f1ff;
}

.spectacledcoder-search-bar:hover {
  width: 100%;
  border-radius: 0% 30px 30px 30px;
}

.spectacledcoder-search-input {
  margin: auto;
  display: none;
  transition: display 12s;
  outline: none;
  border: none;
  font-family: "Belanosima", sans-serif;
  border-radius: 25px;
  padding-left: 10px;
}

.spectacledcoder-search-bar:hover .spectacledcoder-search-input {
  display: inline-flex;
  width: 78%;
  height: 70%;
}

input[type="submit"] {
  display: none;
}

@media only screen and (max-width: 600px) {
  .spectacledcoder-search-bar:hover {
    width: 80%;
    border-radius: 0% 30px 30px 15px;
  }
}

.justify-space-between {
  display: flex;
}

.chat-header-bar-container {
  width: 100%;
  background: #fdfdfd;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eee;
}

.chat-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f9f9f9;
}

.chat-title-group {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 15px;

  .chat-add-icon {
    margin-left: 10px;
    font-size: 18px;
    cursor: pointer;
    transition: transform 0.3s ease;
  }

  .rotated {
    transform: rotate(45deg);
  }
}

.chat-form-accordion {
  padding: 15px;
  background: #ffffff;
  border-top: 1px solid #eee;
}
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.accordion-enter,
.accordion-leave-to {
  height: 0;
  padding: 0;
  opacity: 0;
}
.icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  position: relative;
}

@media screen and (max-width: 768px) {
  .chat-list {
    max-height: 60vh;
  }
}

@media screen and (max-width: 375px) {
  .chat-list {
    max-height: 55vh;
  }
}

.chat-loader {
  text-align: center;
  padding: 10px;
  font-size: 14px;
  color: #999;
}

.not-read-label {
  background-color: #ff4949;
  color: white;
  /* padding: 2px 6px; */
  font-size: 12px;
  border-radius: 10px;
  margin-top: 4px;
  display: inline-block;
  min-width: 18px;
  text-align: center;
}

.no-chat-message {
  font-size: 14px;
  color: #999;
  margin-top: 10px;
  padding: 20px;
  border-radius: 8px;
  background: #f9f9f9;
}

.chat-end-message {
  font-size: 13px;
  color: #888;
  margin-bottom: 10px;
}
</style>
